/**
 * Text formatting utilities for TypographyWMention.
 * Handles URL detection/linking, escape sequences, and WhatsApp-style bold/italic.
 */

const ESCAPED_PLACEHOLDER_PREFIX = '__ESC_';

/**
 * URL pattern: http(s) or www, then non-whitespace, non-angle-bracket (to avoid matching inside HTML).
 * Captures optional trailing punctuation so it can be rendered after the link.
 */
const URL_REGEX = /((?:https?:\/\/|www\.)[^\s<>"']+?)([.,;:!?)\]]*)(?=\s|$)/g;

/**
 * Determines if a string is a valid URL format.
 * Does not use external libraries (e.g. autolinker).
 */
export function isValidUrl(text: string): boolean {
    if (!text || typeof text !== 'string') return false;
    const trimmed = text.trim();
    if (!trimmed) return false;
    try {
        const withProtocol = trimmed.startsWith('http://') || trimmed.startsWith('https://') ? trimmed : `https://${trimmed}`;
        const u = new URL(withProtocol);
        return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
        return false;
    }
}

/**
 * Normalizes a URL for use in href (e.g. www.example.com -> https://www.example.com).
 */
function normalizeUrlForHref(url: string): string {
    const trimmed = url.trim();
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    return `https://${trimmed}`;
}

/**
 * Finds URLs in text and replaces them with anchor tags.
 * Only links plain-text URLs; does not alter content inside existing HTML tags.
 * Uses placeholders to avoid matching URLs inside attribute values.
 */
export function detectAndLinkUrls(text: string): string {
    if (!text || typeof text !== 'string') return text;

    return text.replace(URL_REGEX, (_match, urlPart: string, trailingPunct: string) => {
        if (!isValidUrl(urlPart)) return urlPart + trailingPunct;
        const href = normalizeUrlForHref(urlPart);
        return `<a href="${href}" target="_blank" rel="noopener noreferrer">${escapeHtml(urlPart)}</a>${trailingPunct}`;
    });
}

function escapeHtml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Replaces \* and \_ with placeholders so they are not interpreted as formatting.
 * Returns the modified text and a map of placeholders to restore later.
 */
export function processEscapeSequences(text: string): { text: string; placeholders: Map<string, string> } {
    const placeholders = new Map<string, string>();
    if (!text || typeof text !== 'string') return { text: text || '', placeholders };

    let out = text;
    let index = 0;

    out = out.replace(/\\([*_])/g, (_, char: string) => {
        const key = `${ESCAPED_PLACEHOLDER_PREFIX}${index++}__`;
        placeholders.set(key, char);
        return key;
    });

    return { text: out, placeholders };
}

/**
 * Restores literal * and _ from placeholders after formatting has been applied.
 */
export function restoreEscapeSequences(text: string, placeholders: Map<string, string>): string {
    if (!text || placeholders.size === 0) return text;
    let out = text;
    placeholders.forEach((char, key) => {
        out = out.split(key).join(char);
    });
    return out;
}

interface Delimiter {
    pos: number;
    kind: 'b' | 'i';
}

interface Pair {
    open: number;
    close: number;
    kind: 'b' | 'i';
}

/**
 * First pass: find paired * and _ (same type, proper nesting).
 * Unpaired delimiters are left as literal.
 */
function findFormatPairs(text: string): Pair[] {
    const delims: Delimiter[] = [];
    for (let i = 0; i < text.length; i++) {
        if (text[i] === '*') delims.push({ pos: i, kind: 'b' });
        if (text[i] === '_') delims.push({ pos: i, kind: 'i' });
        if (text[i] === '<') {
            const end = text.indexOf('>', i);
            if (end !== -1) i = end;
        }
    }

    const stack: Delimiter[] = [];
    const pairs: Pair[] = [];

    for (const d of delims) {
        const last = stack.length > 0 ? stack[stack.length - 1] : null;
        if (last && last.kind === d.kind) {
            stack.pop();
            pairs.push({ open: last.pos, close: d.pos, kind: d.kind });
        } else {
            stack.push(d);
        }
    }

    return pairs.sort((a, b) => a.open - b.open);
}

/**
 * Parses WhatsApp-style formatting: *bold* and _italic_.
 * Supports nesting in any order; requires closing delimiters.
 * Unclosed delimiters are left as literal * and _.
 * Skips delimiters inside HTML tags (e.g. inside <a>).
 */
export function parseTextFormatting(text: string): string {
    if (!text || typeof text !== 'string') return text;

    const pairs = findFormatPairs(text);
    const openAt = new Map<number, 'b' | 'i'>();
    const closeAt = new Map<number, 'b' | 'i'>();
    pairs.forEach((p) => {
        openAt.set(p.open, p.kind);
        closeAt.set(p.close, p.kind);
    });

    let result = '';
    let i = 0;
    const len = text.length;

    while (i < len) {
        if (openAt.has(i)) {
            result += openAt.get(i) === 'b' ? '<strong>' : '<em>';
            i += 1;
            continue;
        }
        if (closeAt.has(i)) {
            result += closeAt.get(i) === 'b' ? '</strong>' : '</em>';
            i += 1;
            continue;
        }
        if (text[i] === '<') {
            const close = text.indexOf('>', i);
            if (close !== -1) {
                result += text.slice(i, close + 1);
                i = close + 1;
                continue;
            }
        }
        result += text[i];
        i += 1;
    }

    return result;
}
